import { log } from '@/log'
import { CartItem, CartItemList } from '@/models/cart/cart-item'
import { extractDisplayedPurchasableItems } from '@spa/store/modules/cart/function'

/*
 * https://github.com/my-color/front/issues/3897 のクライアント側要件を達成するためのモジュール.
 * 概要は https://my-color.esa.io/posts/569 を参照.
 *
 * ユーザーが一度に購入する数として、選択可能な個数の範囲を提供する.
 * あくまで「選択可能な個数の範囲」を提示するものであって、違反した場合にどうするかなどの制約は、ここでは特に規定しない.
 *
 * ユーザーへエラー情報をどのように通知し、そのエラー情報をどう管理するかは、
 * このモジュールを使用するモジュール(基本的にはview)が決定すること.
 *
 * FIXME: 最終的に、これらの実装はすべてサーバーサイドへ移植されるべき.
 * 以下の理由から、1stリリースでサーバーサイドに実装することは諦めた.
 * - これをサーバーサイドで実装するには在庫の情報が必要
 *   - 在庫情報を取得するためには、カートリソースだけでなく、SKU情報も別途取得が必要
 *   - SKU情報は、クライアント側ですでに取得している
 *   - おなじことをサーバーサイドで行うと、同じ情報を二重三重に取得することになる
 *   - 関連する詳細実装も、既にクライアント側に記述されている
 *   - 以上の理由から、いきなりサーバーサイドで実装するのはコストが高い.
 * - コンセプト検証としての側面
 *   - 「選択範囲」とそれを提供する「仕様」という組み合わせで、要求を満たせるかの確認実装
 *   - 検証にあたり、PHPよりも型を厳密に定義できるTypeScriptの方が幾分有利.
 *   - クライアント側に実装する理由としては些か弱いが、先述の実装コストも勘案.
 *
 * 十分に要求が達成できることが確認できた上で、クライアントのDTO依存を引き剥がしつつ、
 * 段階的にこの実装をサーバーサイドへ組み込んでいくことが望ましい.
 * その場合、恐らくレスポンスの一部に
 * ```
 * {
 *   "purchase_limit": {
 *     "max": 10,
 *     "min": 1,
 *   }
 * }
 * ```
 * のような値が含まれるようになる想定.
 */

/**
 * 購入個数の下限/上限範囲
 */
export interface PurchaseCountLimitRange {
  max: number
  min: number
}
/**
 * 購入個数上限仕様
 */
export interface PurchaseCountLimitSpecification {
  purchaseCountRangeFor(item: CartItem): PurchaseCountLimitRange
}

/**
 * UseClosestRangeで使う、中間データ構造
 */
interface IntermediateRangeList {
  maxList: number[]
  minList: number[]
}

/**
 * 複数の仕様を組み合わせて、最も狭い範囲の購入個数範囲を決定する.
 */
export class UseClosestRange implements PurchaseCountLimitSpecification {
  constructor(
    private readonly specifications: Readonly<PurchaseCountLimitSpecification[]>
  ) {
  }
  purchaseCountRangeFor(item: CartItem): PurchaseCountLimitRange {
    const { maxList, minList } = this.specifications.reduce(
      (prev: IntermediateRangeList, spec: PurchaseCountLimitSpecification): IntermediateRangeList => {
        const range = spec.purchaseCountRangeFor(item)

        return {
          maxList: [...prev.maxList, range.max],
          minList: [...prev.minList, range.min],
        }
      },
      ({ maxList: [], minList: [] })
    )

    const result = {
      max: Math.min(...maxList),
      min: Math.max(...minList),
    }

    if (result.min > result.max) {
      return { min: 0, max: 0 }
    }

    return result
  }
}
/**
 * SKU単位の購入上限以下まで購入可能
 */
export class AllowPurchaseUntilSkuPurchaseCountLimit implements PurchaseCountLimitSpecification {
  purchaseCountRangeFor(item: CartItem): PurchaseCountLimitRange {
    if (! item.maxSellCount) {
      // 制限が設定されていないなら、在庫のルールに任せる
      return new AllowPurchaseLimitUntilStock().purchaseCountRangeFor(item)
    }

    return {
      max: item.maxSellCount,
      min: 1,
    }
  }
}
/**
 * 在庫以下まで購入可能
 */
export class AllowPurchaseLimitUntilStock implements PurchaseCountLimitSpecification {
  purchaseCountRangeFor(item: CartItem): PurchaseCountLimitRange {
    if (! item.stock) {
      // 本来、stock = SKU単位の在庫には確実にnumberが設定されているべき.
      // しかし、現在は型定義上 optional になっているので、やむなく型定義上 number を返すようにしている.
      // とはいえ、現状の型定義が optional である以上、実際に undefined が渡ってきてしまう危険性は否めない.
      // その際、実際には購入できないのに誤って1個選択できるとしてしまうよりは、0を返して購入不可にした方が、
      // 商品管理上は安全と判断.
      return {
        max: 0,
        min: 0,
      }
    }

    return {
      max: item.stock,
      min: 1,
    }
  }
}
/**
 * ROOTの購入個数 - 同じROOTに紐づく商品のカート内合計個数以下まで購入可能
 */
export class TotalPurchaseCountWithSameRootMustLessThanRootPurchaseLimit implements PurchaseCountLimitSpecification {
  constructor(private readonly wholeCartItems: CartItemList) {}

  purchaseCountRangeFor(item: CartItem): PurchaseCountLimitRange {
    if (! item.rootPurchaseLimit) {
      // 制限が設定されていないなら、在庫のルールに任せる
      return new AllowPurchaseLimitUntilStock().purchaseCountRangeFor(item)
    }

    const otherItemsBelongingToSameRoot =
      new CartItemList(
        extractDisplayedPurchasableItems(this.wholeCartItems.list).filter(
          i => i.id !== item.id && i.rootId === item.rootId
        )
      )

    const max = item.rootPurchaseLimit - otherItemsBelongingToSameRoot.totalCount

    if (max === 0) {
      return { max: 0, min: 0 }
    }

    return {
      max,
      min: 1,
    }
  }
}

/**
 * server側の MustWithinTotalCountLimit に対応する。
 */
export class AllowPurchaseUntilCartTotalCountLimit implements PurchaseCountLimitSpecification {
  constructor(
    private readonly wholeCartItems: CartItemList,
    private readonly limitCartUnitCount: number
  ) {}

  purchaseCountRangeFor(item: CartItem): PurchaseCountLimitRange {
    const otherItems =
      new CartItemList(
        extractDisplayedPurchasableItems(this.wholeCartItems.list).filter(
          i => i.id !== item.id
        )
      )

    const max = this.limitCartUnitCount - otherItems.totalCount

    if (max <= 0) {
      return { max: 0, min: 0 }
    }

    return {
      max,
      min: 1,
    }
  }
}

/**
 * 会員価格での購入には上限数が設定される。
 * 購入しようとしている価格での購入上限まで購入可能
 */
export class AllowPurchaseUntilRestAmountOfPrice implements PurchaseCountLimitSpecification {
  purchaseCountRangeFor(item: CartItem): PurchaseCountLimitRange {
    if (!item.currentValidPrice) {
      log.warn({
        message: `item.currentValidPrice is not set.`,
        item,
      })

      /**
       * currentValidPriceが存在しない = 価格の検証が行われていない場合はエラーと言えるが、
       * このモジュールの責務ではないので「未設定」扱いとし、在庫のルールに任せる
       */
      return new AllowPurchaseLimitUntilStock().purchaseCountRangeFor(item)
    }

    return {
      max: item.unitCount + item.currentValidPrice.prices.selected.rest_amount.add_to_cart,
      min: 1,
    }
  }
}
